import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { GUEST_INVITES } from "@/inviteList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rt_admin_token";

const formatDate = (d) => {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
};

const csvEscape = (v) => {
  const s = (v ?? "").toString().replace(/"/g, '""');
  return `"${s}"`;
};

const downloadCSV = (rows) => {
  const header = [
    "Guest name(s)",
    "Email",
    "Attendance",
    "Dietary",
    "Song",
    "Message",
    "Submitted",
  ];
  const lines = [header.map(csvEscape).join(",")];
  rows.forEach((r) => {
    lines.push(
      [
        r.guest_names,
        r.email,
        r.attendance === "accept" ? "Accept" : "Decline",
        r.dietary,
        r.song,
        r.message,
        formatDate(r.created_at),
      ]
        .map(csvEscape)
        .join(",")
    );
  });
  const blob = new Blob(["\uFEFF" + lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const Login = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back");
      onSuccess(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-shell login-shell">
      <form className="admin-card login-card" onSubmit={submit}>
        <div className="admin-brand">
          <span className="admin-mono">R <em>&amp;</em> T</span>
          <span className="admin-brand-sub">Wedding Admin</span>
        </div>
        <h1 className="admin-h1">Welcome</h1>
        <p className="admin-sub">Please enter your password to view RSVPs.</p>
        <label className="admin-label">
          Password
          <input
            type="password"
            className="admin-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoFocus
            required
            data-testid="admin-password"
          />
        </label>
        <button className="admin-btn primary" type="submit" disabled={busy} data-testid="admin-login-btn">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <a className="admin-back" href="/">← Back to invitation</a>
      </form>
    </div>
  );
};

const relativeTime = (d) => {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  return formatDate(d);
};

const InviteTracking = ({ authHeader, onUnauthorized }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // slug expanded to show recent opens

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/invites/stats`, { headers: authHeader });
      setStats(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error("Could not load invitation activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const bySlug = Object.fromEntries(stats.map((s) => [s.slug, s]));
    return GUEST_INVITES.map((g) => ({
      ...g,
      stat: bySlug[g.slug] || null,
    })).sort((a, b) => {
      const la = a.stat?.last_viewed ? new Date(a.stat.last_viewed).getTime() : 0;
      const lb = b.stat?.last_viewed ? new Date(b.stat.last_viewed).getTime() : 0;
      return lb - la;
    });
  }, [stats]);

  const openedCount = rows.filter((r) => r.stat).length;

  const weekend = useMemo(() => {
    const bySlug = Object.fromEntries(stats.map((s) => [s.slug, s]));
    return { opens: bySlug["weekend"] || null, saves: bySlug["weekend-calendar"] || null };
  }, [stats]);

  return (
    <section className="admin-invites" data-testid="invite-tracking">
      <div className="admin-section-head">
        <div>
          <span className="drawer-eyebrow">Our Wedding Weekend page</span>
          <h2 className="admin-h2">Weekend schedule</h2>
        </div>
        <a className="admin-textlink" href="/ourweddingweekend" target="_blank" rel="noreferrer">
          /ourweddingweekend →
        </a>
      </div>
      {loading ? (
        <div className="admin-empty">Loading…</div>
      ) : (
        <div className="admin-stats weekend-stats" data-testid="weekend-tracking">
          <div className="stat">
            <div className="stat-value" data-testid="weekend-opens">
              {weekend.opens?.views || 0}
            </div>
            <div className="stat-label">Schedule opened</div>
            <div className="stat-foot">
              {weekend.opens ? `Last ${relativeTime(weekend.opens.last_viewed)}` : "Not yet opened"}
            </div>
          </div>
          <div className="stat messages">
            <div className="stat-value" data-testid="weekend-saves">
              {weekend.saves?.views || 0}
            </div>
            <div className="stat-label">Saved to calendar</div>
            <div className="stat-foot">
              {weekend.saves ? `Last ${relativeTime(weekend.saves.last_viewed)}` : "No saves yet"}
            </div>
          </div>
          <div className="stat">
            <div className="stat-value">
              {weekend.opens?.views
                ? `${Math.round(((weekend.saves?.views || 0) / weekend.opens.views) * 100)}%`
                : "—"}
            </div>
            <div className="stat-label">Save rate</div>
            <div className="stat-foot">of guests who opened it</div>
          </div>
          <div className="stat">
            <div className="stat-value">
              {weekend.opens ? formatDate(weekend.opens.first_viewed).split(",")[0] : "—"}
            </div>
            <div className="stat-label">First opened</div>
            <div className="stat-foot">
              {weekend.opens ? formatDate(weekend.opens.first_viewed).split(", ")[1] || "" : ""}
            </div>
          </div>
        </div>
      )}

      <div className="admin-section-head invites-head">
        <div>
          <span className="drawer-eyebrow">Personalised invitations</span>
          <h2 className="admin-h2">
            {openedCount} of {GUEST_INVITES.length} opened
          </h2>
        </div>
        <button className="admin-btn ghost" onClick={load} data-testid="invites-refresh">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="admin-empty">Loading…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table invites-table">
            <thead>
              <tr>
                <th>Guest</th>
                <th>Invitation</th>
                <th>Link</th>
                <th>Status</th>
                <th>First opened</th>
                <th>Last opened</th>
                <th>Opens</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const opened = !!r.stat;
                const expanded = open === r.slug;
                return (
                  <React.Fragment key={r.slug}>
                    <tr
                      className={`admin-row ${opened ? "" : "muted"}`}
                      onClick={() => opened && setOpen(expanded ? null : r.slug)}
                      data-testid={`invite-row-${r.slug}`}
                    >
                      <td className="cell-name">{r.guest}</td>
                      <td className="cell-song">{r.type}</td>
                      <td className="cell-email">
                        <a
                          href={r.paths[0]}
                          target="_blank"
                          rel="noreferrer"
                          className="admin-textlink"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.paths[0]}
                        </a>
                      </td>
                      <td>
                        <span
                          className={`badge ${opened ? "accept" : "pending"}`}
                          data-testid={`invite-status-${r.slug}`}
                        >
                          {opened ? "Opened" : "Not yet opened"}
                        </span>
                      </td>
                      <td className="cell-date">{opened ? formatDate(r.stat.first_viewed) : "—"}</td>
                      <td className="cell-date">
                        {opened ? (
                          <>
                            {formatDate(r.stat.last_viewed)}
                            <span className="cell-rel"> · {relativeTime(r.stat.last_viewed)}</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="cell-count">
                        {opened ? r.stat.views : "0"}
                        {opened ? <span className="cell-cta"> {expanded ? "▾" : "›"}</span> : null}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="invite-detail-row">
                        <td colSpan={7}>
                          <ul className="invite-detail-list">
                            {r.stat.recent.map((v) => (
                              <li key={v.id}>
                                <span>{formatDate(v.viewed_at)}</span>
                                <span className="cell-rel">{v.device || "Unknown device"}</span>
                              </li>
                            ))}
                            {r.stat.views > r.stat.recent.length && (
                              <li className="cell-rel">
                                + {r.stat.views - r.stat.recent.length} earlier open
                                {r.stat.views - r.stat.recent.length > 1 ? "s" : ""}
                              </li>
                            )}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | accept | decline | messages
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/rsvp`, { headers: authHeader });
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
        toast.error("Session expired — please sign in again");
      } else {
        toast.error("Could not load RSVPs");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = rows;
    if (filter === "accept") list = list.filter((r) => r.attendance === "accept");
    else if (filter === "decline") list = list.filter((r) => r.attendance === "decline");
    else if (filter === "messages") list = list.filter((r) => (r.message || "").trim().length > 0);
    const query = q.trim().toLowerCase();
    if (query) {
      list = list.filter((r) =>
        [r.guest_names, r.email, r.dietary, r.song, r.message]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(query))
      );
    }
    return list;
  }, [rows, filter, q]);

  const stats = useMemo(() => {
    const accepted = rows.filter((r) => r.attendance === "accept").length;
    const declined = rows.filter((r) => r.attendance === "decline").length;
    const withMessage = rows.filter((r) => (r.message || "").trim().length > 0).length;
    return { total: rows.length, accepted, declined, withMessage };
  }, [rows]);

  const remove = async (id) => {
    if (!window.confirm("Delete this RSVP permanently?")) return;
    try {
      await axios.delete(`${API}/rsvp/${id}`, { headers: authHeader });
      setRows((r) => r.filter((x) => x.id !== id));
      setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div className="admin-brand">
          <span className="admin-mono">R <em>&amp;</em> T</span>
          <span className="admin-brand-sub">Wedding Admin</span>
        </div>
        <div className="admin-header-actions">
          <a className="admin-textlink" href="/">View site →</a>
          <button className="admin-btn ghost" onClick={fetchRows}>Refresh</button>
          <button className="admin-btn ghost" onClick={onLogout} data-testid="admin-logout">Sign out</button>
        </div>
      </header>

      <section className="admin-stats">
        <div className="stat">
          <div className="stat-value" data-testid="stat-total">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat accepted">
          <div className="stat-value" data-testid="stat-accept">{stats.accepted}</div>
          <div className="stat-label">Joyfully accept</div>
        </div>
        <div className="stat declined">
          <div className="stat-value" data-testid="stat-decline">{stats.declined}</div>
          <div className="stat-label">Regretfully decline</div>
        </div>
        <div className="stat messages">
          <div className="stat-value">{stats.withMessage}</div>
          <div className="stat-label">With a message</div>
        </div>
      </section>

      <section className="admin-toolbar">
        <div className="filters">
          {[
            { k: "all", label: "All" },
            { k: "accept", label: "Accepting" },
            { k: "decline", label: "Declining" },
            { k: "messages", label: "With message" },
          ].map((f) => (
            <button
              key={f.k}
              className={`chip ${filter === f.k ? "active" : ""}`}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="search-wrap">
          <input
            type="search"
            className="admin-input search"
            placeholder="Search name, email, song, message…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button
          className="admin-btn primary"
          onClick={() => downloadCSV(filtered)}
          disabled={filtered.length === 0}
          data-testid="export-csv"
        >
          Export CSV
        </button>
      </section>

      <section className="admin-table-wrap">
        {loading ? (
          <div className="admin-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-glyph">✽</div>
            No RSVPs match this view yet.
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Guest name(s)</th>
                <th>Email</th>
                <th>Reply</th>
                <th>Song</th>
                <th>Dietary</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} onClick={() => setSelected(r)} className="admin-row">
                  <td className="cell-name">
                    {r.guest_names}
                    {r.message ? <span className="has-msg" title="Left a message">✽</span> : null}
                  </td>
                  <td className="cell-email">{r.email}</td>
                  <td>
                    <span className={`badge ${r.attendance}`}>
                      {r.attendance === "accept" ? "Accept" : "Decline"}
                    </span>
                  </td>
                  <td className="cell-song">{r.song || "—"}</td>
                  <td className="cell-dietary">{r.dietary || "—"}</td>
                  <td className="cell-date">{formatDate(r.created_at)}</td>
                  <td className="cell-cta">›</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <InviteTracking authHeader={authHeader} onUnauthorized={onLogout} />

      {selected && (
        <div className="admin-drawer-backdrop" onClick={() => setSelected(null)}>
          <aside className="admin-drawer" onClick={(e) => e.stopPropagation()}>
            <button className="drawer-close" onClick={() => setSelected(null)} aria-label="Close">
              ×
            </button>
            <span className="drawer-eyebrow">RSVP details</span>
            <h2 className="drawer-name">{selected.guest_names}</h2>
            <div className={`badge lg ${selected.attendance}`}>
              {selected.attendance === "accept" ? "Joyfully accept ✽" : "Regretfully decline"}
            </div>

            <dl className="drawer-list">
              <dt>Email</dt>
              <dd>
                <a href={`mailto:${selected.email}`} className="admin-textlink">
                  {selected.email}
                </a>
              </dd>
              <dt>Song request</dt>
              <dd>{selected.song || "—"}</dd>
              <dt>Dietary</dt>
              <dd>{selected.dietary || "—"}</dd>
              <dt>Message</dt>
              <dd className="drawer-msg">{selected.message || "—"}</dd>
              <dt>Submitted</dt>
              <dd>{formatDate(selected.created_at)}</dd>
            </dl>

            <div className="drawer-actions">
              <a
                className="admin-btn ghost"
                href={`mailto:${selected.email}?subject=Thank%20you%20for%20your%20RSVP`}
              >
                Reply by email
              </a>
              <button
                className="admin-btn danger"
                onClick={() => remove(selected.id)}
                data-testid="delete-rsvp"
              >
                Delete
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setChecking(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <>
      {checking ? (
        <div className="admin-shell login-shell">
          <div className="admin-empty">Loading…</div>
        </div>
      ) : token ? (
        <Dashboard token={token} onLogout={logout} />
      ) : (
        <Login onSuccess={setToken} />
      )}
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#f8f3ea",
            border: "1px solid #cbb280",
            color: "#2e523f",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "15px",
          },
        }}
      />
    </>
  );
};

export default Admin;
